<template>
  <section class="empleado-panel">
    <div v-if="error" class="mod-alert-error">
      <i class="bi bi-exclamation-triangle-fill"></i>
      {{ error }}
    </div>

    <div class="empleado-security-note">
      <i class="bi bi-shield-lock"></i>
      <span>Esta sección contiene datos personales y bancarios. Úsala solo para operación autorizada.</span>
    </div>

    <form class="ext-form" @submit.prevent="guardar">

      <!-- ===== IDENTIFICACIÓN PERSONAL ===== -->
      <div class="form-section">
        <div class="form-section-hdr">
          <i class="bi bi-person-badge"></i>
          Identificación personal
        </div>
        <div class="form-g3">
          <div class="mod-field form-span-3">
            <label>Nombre legal</label>
            <input v-model="form.NombreLegal" type="text" maxlength="180"
              placeholder="Nombre como aparece en documentos oficiales">
          </div>

          <div class="mod-field">
            <label>CURP</label>
            <input v-model="form.Curp" type="text" maxlength="18" placeholder="18 caracteres"
              class="ext-monospace" @input="form.Curp = form.Curp.toUpperCase()">
            <span v-if="errores.Curp" class="campo-error">{{ errores.Curp }}</span>
          </div>
          <div class="mod-field">
            <label>RFC</label>
            <input v-model="form.Rfc" type="text" maxlength="13" placeholder="Con homoclave"
              class="ext-monospace" @input="form.Rfc = form.Rfc.toUpperCase()">
            <span v-if="errores.Rfc" class="campo-error">{{ errores.Rfc }}</span>
          </div>
          <div class="mod-field">
            <label>NSS</label>
            <input v-model="form.Nss" type="text" maxlength="20" placeholder="11 dígitos"
              class="ext-monospace">
            <span v-if="errores.Nss" class="campo-error">{{ errores.Nss }}</span>
          </div>

          <div class="mod-field">
            <label>Tipo de identificación</label>
            <CatSearch catalogo="empleado_identificacion_tipo" v-model="form.TipoIdentificacion"
              placeholder="Buscar tipo..." display-field="nombre" :min-length="0"/>
          </div>
          <div class="mod-field">
            <label>Número de identificación</label>
            <input v-model="form.NumeroIdentificacion" type="text" maxlength="80"
              placeholder="Folio, OCR, pasaporte, cédula...">
          </div>
          <div class="mod-field">
            <label>Vigencia de identificación</label>
            <input v-model="form.FechaVencimientoIdentificacion" type="date">
          </div>

          <div class="mod-field">
            <label>Fecha de nacimiento</label>
            <input v-model="form.FechaNacimiento" type="date">
          </div>
          <div class="mod-field">
            <label>Sexo</label>
            <select v-model="form.Sexo">
              <option value="">— Sin especificar —</option>
              <option value="Masculino">Masculino</option>
              <option value="Femenino">Femenino</option>
              <option value="Indistinto">Indistinto</option>
              <option value="Prefiero no especificar">Prefiero no especificar</option>
            </select>
          </div>
          <div class="mod-field">
            <label>Estado civil</label>
            <input v-model="form.EstadoCivil" type="text" maxlength="30"
              placeholder="Soltero, casado, unión libre...">
          </div>

          <div class="mod-field">
            <label>Nacionalidad</label>
            <input v-model="form.Nacionalidad" type="text" maxlength="80" placeholder="Mexicana, extranjera...">
          </div>
          <div class="mod-field form-span-2">
            <label>Lugar de nacimiento</label>
            <input v-model="form.LugarNacimiento" type="text" maxlength="120"
              placeholder="Ciudad, Estado, País">
          </div>
        </div>
      </div>

      <!-- ===== DOMICILIO ===== -->
      <div class="form-section">
        <div class="form-section-hdr">
          <i class="bi bi-house"></i>
          Domicilio
        </div>
        <div class="form-g3">
          <div class="mod-field form-span-2">
            <label>Calle y número</label>
            <input v-model="form.CalleYNumero" type="text" maxlength="180"
              placeholder="Calle, número exterior e interior">
          </div>
          <div class="mod-field">
            <label>Código postal</label>
            <input v-model="form.CodigoPostal" type="text" maxlength="10" placeholder="C.P." class="ext-monospace">
            <span v-if="errores.CodigoPostal" class="campo-error">{{ errores.CodigoPostal }}</span>
          </div>
          <div class="mod-field">
            <label>Colonia</label>
            <input v-model="form.Colonia" type="text" maxlength="120" placeholder="Colonia">
          </div>
          <div class="mod-field">
            <label>Ciudad</label>
            <input v-model="form.Ciudad" type="text" maxlength="120" placeholder="Ciudad">
          </div>
          <div class="mod-field">
            <label>Estado</label>
            <input v-model="form.Estado" type="text" maxlength="120" placeholder="Estado / Provincia">
          </div>
          <div class="mod-field">
            <label>País</label>
            <input v-model="form.Pais" type="text" maxlength="80" placeholder="México">
          </div>
        </div>
      </div>

      <!-- ===== CONTACTO Y EMERGENCIA ===== -->
      <div class="form-section">
        <div class="form-section-hdr">
          <i class="bi bi-telephone"></i>
          Contacto y emergencia
        </div>
        <div class="form-g2">
          <div class="mod-field">
            <label>Teléfono personal</label>
            <input v-model="form.TelefonoPersonal" type="text" maxlength="40" placeholder="Teléfono móvil o fijo">
          </div>
          <div class="mod-field">
            <label>Teléfono alterno</label>
            <input v-model="form.TelefonoAlterno" type="text" maxlength="40" placeholder="Teléfono adicional">
          </div>
          <div class="mod-field">
            <label>Correo personal</label>
            <input v-model="form.CorreoPersonal" type="text" maxlength="120" placeholder="correo@personal.com">
            <span v-if="errores.CorreoPersonal" class="campo-error">{{ errores.CorreoPersonal }}</span>
          </div>
          <div class="mod-field">
            <label>Correo corporativo</label>
            <input v-model="form.CorreoCorporativo" type="text" maxlength="120" placeholder="nombre@empresa.com">
            <span v-if="errores.CorreoCorporativo" class="campo-error">{{ errores.CorreoCorporativo }}</span>
          </div>
        </div>
        <div class="ext-subsection-hdr">Contacto de emergencia</div>
        <div class="form-g3">
          <div class="mod-field form-span-2">
            <label>Nombre</label>
            <input v-model="form.ContactoEmergenciaNombre" type="text" maxlength="180"
              placeholder="Nombre completo de la persona">
          </div>
          <div class="mod-field">
            <label>Parentesco</label>
            <input v-model="form.ContactoEmergenciaParentesco" type="text" maxlength="80"
              placeholder="Madre, padre, cónyuge...">
          </div>
          <div class="mod-field">
            <label>Teléfono</label>
            <input v-model="form.ContactoEmergenciaTelefono" type="text" maxlength="40"
              placeholder="Teléfono de contacto inmediato">
          </div>
        </div>
      </div>

      <!-- ===== LABORAL ===== -->
      <div class="form-section">
        <div class="form-section-hdr">
          <i class="bi bi-briefcase"></i>
          Datos laborales
        </div>
        <div class="form-g3">
          <div class="mod-field">
            <label>Fecha de ingreso</label>
            <input v-model="form.FechaIngreso" type="date">
          </div>
          <div class="mod-field">
            <label>Fecha de baja</label>
            <input v-model="form.FechaBaja" type="date">
            <span v-if="errores.FechaBaja" class="campo-error">{{ errores.FechaBaja }}</span>
          </div>
          <div class="mod-field">
            <label>Tipo de contrato</label>
            <input v-model="form.TipoContrato" type="text" maxlength="80"
              placeholder="Indeterminado, temporal, honorarios...">
          </div>

          <div class="mod-field">
            <label>Puesto</label>
            <CatSearch catalogo="puesto" v-model="form.Puesto"
              placeholder="Buscar puesto..." display-field="nombre" :min-length="0"/>
          </div>
          <div class="mod-field">
            <label>Departamento</label>
            <CatSearch catalogo="departamento" v-model="form.Departamento"
              placeholder="Buscar departamento..." display-field="nombre" :min-length="0"/>
          </div>
          <div class="mod-field">
            <label>Jefe directo</label>
            <CatSearch catalogo="empleado" v-model="form.JefeDirectoClave"
              placeholder="Buscar empleado..." display-field="nombre" :min-length="0"/>
          </div>

          <div class="mod-field">
            <label>Turno</label>
            <CatSearch catalogo="turno" v-model="form.Turno"
              placeholder="Buscar turno..." display-field="nombre" :min-length="0"/>
          </div>
          <div class="mod-field">
            <label>Jornada</label>
            <input v-model="form.Jornada" type="text" maxlength="80"
              placeholder="Completa, parcial, mixta...">
          </div>
          <div class="mod-field">
            <label>Estatus laboral</label>
            <input v-model="form.EstatusLaboral" type="text" maxlength="50"
              placeholder="Activo, incapacidad, baja...">
          </div>

          <div v-if="form.FechaBaja" class="mod-field form-span-3">
            <label>Motivo de baja</label>
            <input v-model="form.MotivoBaja" type="text" maxlength="255"
              placeholder="Renuncia voluntaria, liquidación, jubilación...">
          </div>
        </div>
      </div>

      <!-- ===== FISCAL Y BANCARIO ===== -->
      <div class="form-section">
        <div class="form-section-hdr">
          <i class="bi bi-bank"></i>
          Fiscal y bancario
        </div>
        <div class="form-g3">
          <div class="mod-field form-span-2">
            <label>Régimen fiscal</label>
            <CatSearch catalogo="sat_regimen_fiscal_fisica" v-model="form.RegimenFiscal"
              placeholder="Buscar régimen fiscal..." display-field="nombre" :min-length="0"/>
          </div>
          <div class="mod-field">
            <label>Código postal fiscal</label>
            <input v-model="form.CodigoPostalFiscal" type="text" maxlength="10"
              placeholder="C.P. domicilio fiscal" class="ext-monospace">
            <span v-if="errores.CodigoPostalFiscal" class="campo-error">{{ errores.CodigoPostalFiscal }}</span>
          </div>

          <div class="mod-field form-span-2">
            <label>Banco</label>
            <CatSearch catalogo="banco" v-model="form.BancoClave"
              placeholder="Buscar banco..." display-field="nombre" :min-length="0"/>
          </div>
          <div class="mod-field">
            <label>Forma de pago</label>
            <input v-model="form.FormaPago" type="text" maxlength="80"
              placeholder="Transferencia, cheque, efectivo...">
          </div>

          <div class="mod-field">
            <label>Cuenta bancaria</label>
            <input v-model="form.CuentaBancaria" type="text" maxlength="40"
              placeholder="Número de cuenta" class="ext-monospace">
          </div>
          <div class="mod-field">
            <label>CLABE interbancaria</label>
            <input v-model="form.Clabe" type="text" maxlength="18"
              placeholder="18 dígitos" class="ext-monospace">
            <span v-if="errores.Clabe" class="campo-error">{{ errores.Clabe }}</span>
          </div>
          <div class="mod-field">
            <label>Número de tarjeta</label>
            <input v-model="form.NumeroTarjeta" type="text" maxlength="40"
              placeholder="Tarjeta de nómina" class="ext-monospace">
          </div>
        </div>
      </div>

      <!-- ===== SALUD Y UNIFORMES ===== -->
      <div class="form-section">
        <div class="form-section-hdr">
          <i class="bi bi-heart-pulse"></i>
          Salud y uniformes
        </div>
        <div class="form-g3">
          <div class="mod-field">
            <label>Tipo de sangre</label>
            <select v-model="form.TipoSangre">
              <option value="">— Sin especificar —</option>
              <option v-for="t in tiposSangre" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="mod-field form-span-2">
            <label>Alergias</label>
            <input v-model="form.Alergias" type="text" maxlength="255"
              placeholder="Medicamentos, alimentos u otras alergias">
          </div>
          <div class="mod-field form-span-3">
            <label>Condiciones médicas relevantes</label>
            <input v-model="form.CondicionesMedicas" type="text" maxlength="255"
              placeholder="Información médica para seguridad laboral (solo la necesaria)">
          </div>
        </div>
        <div class="ext-subsection-hdr">Tallas de uniformes</div>
        <div class="form-g3">
          <div class="mod-field">
            <label>Talla de camisa</label>
            <input v-model="form.TallaCamisa" type="text" maxlength="20" placeholder="CH, M, G, XG...">
          </div>
          <div class="mod-field">
            <label>Talla de pantalón</label>
            <input v-model="form.TallaPantalon" type="text" maxlength="20" placeholder="28, 30, 32...">
          </div>
          <div class="mod-field">
            <label>Talla de calzado</label>
            <input v-model="form.TallaCalzado" type="text" maxlength="20" placeholder="25, 26.5, 27...">
          </div>
        </div>
      </div>

      <!-- ===== NOTAS ===== -->
      <div class="form-section form-section-last">
        <div class="form-section-hdr">
          <i class="bi bi-journal-text"></i>
          Notas internas
        </div>
        <div class="mod-field">
          <textarea v-model="form.Notas" rows="3"
            placeholder="Observaciones internas autorizadas sobre el empleado. Evita capturar información innecesaria.">
          </textarea>
        </div>
      </div>

      <div class="empleado-panel-actions">
        <span v-if="totalErrores > 0" class="ext-errors-count">
          <i class="bi bi-exclamation-circle"></i>
          {{ totalErrores }} campo(s) con error
        </span>
        <button type="submit" class="mod-btn mod-btn-save" :disabled="saving">
          <span v-if="saving" class="mod-spinner-sm"></span>
          <template v-else>
            <i class="bi bi-check-lg"></i>
            Guardar perfil extendido
          </template>
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import { showToast } from '@/shared/utils/toast';
import CatSearch from '@/shared/components/CatSearch.vue';
import empleadoGestionService from './empleadoGestion.service';

const RE_CURP  = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[\dA-Z]\d$/
const RE_RFC   = /^[A-Z&Ñ]{3,4}\d{6}[A-Z\d]{2}[A-Z\d]$/
const RE_NSS   = /^\d{11}$/
const RE_CLABE = /^\d{18}$/
const RE_CP    = /^\d{5}$/
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const TIPOS_SANGRE = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

function createExt(clave) {
  return {
    Clave: clave,
    EmpresaId: 0,
    NombreLegal: '',
    Curp: '',
    Rfc: '',
    Nss: '',
    TipoIdentificacion: '',
    NumeroIdentificacion: '',
    FechaVencimientoIdentificacion: '',
    FechaNacimiento: '',
    Sexo: '',
    EstadoCivil: '',
    Nacionalidad: '',
    LugarNacimiento: '',
    TelefonoPersonal: '',
    TelefonoAlterno: '',
    CorreoPersonal: '',
    CorreoCorporativo: '',
    CalleYNumero: '',
    Colonia: '',
    CodigoPostal: '',
    Ciudad: '',
    Estado: '',
    Pais: '',
    ContactoEmergenciaNombre: '',
    ContactoEmergenciaParentesco: '',
    ContactoEmergenciaTelefono: '',
    FechaIngreso: '',
    FechaBaja: '',
    TipoContrato: '',
    Puesto: '',
    Departamento: '',
    JefeDirectoClave: '',
    Turno: '',
    Jornada: '',
    EstatusLaboral: '',
    MotivoBaja: '',
    RegimenFiscal: '',
    CodigoPostalFiscal: '',
    BancoClave: '',
    BancoNombre: '',
    CuentaBancaria: '',
    Clabe: '',
    NumeroTarjeta: '',
    FormaPago: '',
    TipoSangre: '',
    Alergias: '',
    CondicionesMedicas: '',
    TallaCamisa: '',
    TallaPantalon: '',
    TallaCalzado: '',
    Notas: ''
  };
}

function dateOnly(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

const DATE_FIELDS = [
  'FechaNacimiento', 'FechaVencimientoIdentificacion',
  'FechaIngreso', 'FechaBaja'
];

export default {
  name: 'EmpleadoExtendidoPanel',
  components: { CatSearch },
  props: {
    empleadoClave: { type: String, required: true }
  },
  data() {
    return {
      form: createExt(this.empleadoClave),
      tiposSangre: TIPOS_SANGRE,
      saving: false,
      error: null,
      errores: {}
    };
  },
  computed: {
    totalErrores() {
      return Object.keys(this.errores).length;
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this.totalErrores > 0) this.errores = {};
      }
    }
  },
  created() {
    this.cargar();
  },
  methods: {
    async cargar() {
      try {
        const data = await empleadoGestionService.leerExtendido(this.empleadoClave);
        this.form = { ...createExt(this.empleadoClave), ...(data || {}) };
        DATE_FIELDS.forEach(f => { this.form[f] = dateOnly(this.form[f]); });
      } catch {
        this.error = 'No fue posible cargar el perfil extendido';
      }
    },

    validarCampos() {
      const e = {};
      const f = this.form;

      if (f.Curp) {
        const curp = f.Curp.trim().toUpperCase();
        f.Curp = curp;
        if (!RE_CURP.test(curp))
          e.Curp = 'CURP inválida — debe tener 18 caracteres con el formato oficial.';
      }
      if (f.Rfc) {
        const rfc = f.Rfc.trim().toUpperCase();
        f.Rfc = rfc;
        if (!RE_RFC.test(rfc))
          e.Rfc = 'RFC inválido — verifica formato y homoclave (12-13 caracteres).';
      }
      if (f.Nss && !RE_NSS.test(f.Nss.trim()))
        e.Nss = 'NSS inválido — deben ser exactamente 11 dígitos numéricos.';

      if (f.Clabe && !RE_CLABE.test(f.Clabe.trim()))
        e.Clabe = 'CLABE inválida — deben ser exactamente 18 dígitos numéricos.';

      if (f.CorreoPersonal && !RE_EMAIL.test(f.CorreoPersonal.trim()))
        e.CorreoPersonal = 'Correo personal con formato inválido.';
      if (f.CorreoCorporativo && !RE_EMAIL.test(f.CorreoCorporativo.trim()))
        e.CorreoCorporativo = 'Correo corporativo con formato inválido.';

      if (f.CodigoPostal && !RE_CP.test(f.CodigoPostal.trim()))
        e.CodigoPostal = 'Código postal debe tener 5 dígitos.';
      if (f.CodigoPostalFiscal && !RE_CP.test(f.CodigoPostalFiscal.trim()))
        e.CodigoPostalFiscal = 'Código postal fiscal debe tener 5 dígitos.';

      if (f.FechaIngreso && f.FechaBaja && f.FechaBaja < f.FechaIngreso)
        e.FechaBaja = 'La fecha de baja no puede ser anterior a la fecha de ingreso.';

      this.errores = e;
      return Object.keys(e).length === 0;
    },

    async guardar() {
      if (!this.validarCampos()) return;
      this.saving = true;
      this.error = null;
      try {
        const result = await empleadoGestionService.guardarExtendido(this.form);
        if (result.Number >= 0) {
          showToast(result.Message || 'Perfil extendido guardado', result.Type || 'success');
          await this.cargar();
        } else {
          this.error = result.Message || 'Error al guardar';
        }
      } catch {
        this.error = 'Error de conexión con el servidor';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";
@import "./empleadoGestion.css";

/* ── Contenedor del formulario ──────────────────────── */
.ext-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Secciones ──────────────────────────────────────── */
.form-section {
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 0.875rem 1rem 1rem;
  background: #ffffff;
  margin-bottom: 0.75rem;
}

.form-section.form-section-last {
  margin-bottom: 0;
}

.form-section-hdr {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--t-primary, #1272c4);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 0.875rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #edf2f7;
}

.form-section-hdr i { font-size: 0.8rem; }

/* Sub-encabezado de grupo dentro de sección */
.ext-subsection-hdr {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0.85rem 0 0.6rem;
}

/* ── Grid layouts ───────────────────────────────────── */
.form-g2,
.form-g3 {
  display: grid;
  gap: 0.75rem 1rem;
  margin-bottom: 0.5rem;
}

.form-g2 { grid-template-columns: repeat(2, 1fr); }
.form-g3 { grid-template-columns: repeat(3, 1fr); }

.form-g2:last-child,
.form-g3:last-child { margin-bottom: 0; }

.form-span-2 { grid-column: span 2; }
.form-span-3 { grid-column: span 3; }

/* ── Textarea ───────────────────────────────────────── */
.mod-field textarea {
  width: 100%;
  padding: 0.6rem 0.85rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  color: #2d3748;
  resize: vertical;
  background: #fff;
  transition: border-color 0.2s;
}

.mod-field textarea:focus {
  outline: none;
  border-color: var(--t-border-focus, #1272c4);
  box-shadow: 0 0 0 3px var(--t-primary-ring, rgba(18, 114, 196, 0.1));
}

/* ── Select deshabilitado ───────────────────────────── */
.mod-field select:disabled {
  background: #f7fafc;
  color: #a0aec0;
  cursor: not-allowed;
}

/* ── Monospace (CURP, RFC, CLABE...) ────────────────── */
.ext-monospace {
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 0.05em;
}

/* ── Errores de validación inline ───────────────────── */
.campo-error {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #c53030;
  line-height: 1.3;
}

/* ── Contador de errores en footer ─────────────────── */
.ext-errors-count {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #c53030;
  margin-right: auto;
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 900px) {
  .form-g3 { grid-template-columns: repeat(2, 1fr); }
  .form-span-3 { grid-column: span 2; }
}

@media (max-width: 640px) {
  .form-g2,
  .form-g3 { grid-template-columns: 1fr; }
  .form-span-2,
  .form-span-3 { grid-column: span 1; }
}
</style>
